import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FormatUrl from "../../utils/UrlFormatter";
import { toast } from "react-toastify";
import { FaStripe, FaCheckCircle, FaTimesCircle, FaSpinner } from "react-icons/fa";
import "./styles/StripeOnboarding.css";
import { getClinicId } from "../../utils/config";

const StripeOnboarding = ({ user }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [accountData, setAccountData] = useState(null);
  // const [chargesEnabled, setChargesEnabled] = useState(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  /**Fetch CSRF Token */
  const fetchCsrfToken = async () => {
    try {
      const csrfApiUrl = FormatUrl("/csrf-token/");
      const csrfResponse = await fetch(csrfApiUrl, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!csrfResponse.ok) throw new Error("Failed to fetch CSRF token");

      const csrfData = await csrfResponse.json();
      return csrfData.csrfToken;
    } catch (error) {
      console.error("CSRF Token Error:", error);
      toast.error("CSRF token fetch failed.");
      return null;
    }
  };

  
  /**Check Stripe Account Status */
  const checkStripeAccountStatus = useCallback(async () => {
    try {
      // setLoading(true);
      const clinicId = getClinicId(user) || searchParams.get("clinic_id");

      if (!clinicId) {
        console.error("Missing clinic_id");
        toast.error("Clinic ID is required.");
        setLoading(false);
        return;
      }

      const finalClinicId = clinicId ?? getClinicId(user);
      const apiUrl = FormatUrl(`/check-account-status/?clinic_id=${finalClinicId}`);
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to check Stripe status");

      const data = await response.json();
      setAccountData(data);
      setStatus(data.charges_enabled ? "connected" : "not_connected");
    //   setChargesEnabled(data.charges_enabled);

      //Propagate `chargesEnabled` to parent (App.js)
      // updateChargesEnabled(data.charges_enabled);
    } catch (error) {
      console.error("Error checking Stripe status:", error);
      setStatus("not_connected");
    } finally {
      setLoading(false);
    }
  }, [searchParams, user]);

  
  /** Handle Onboarding Start */
  const startOnboarding = async () => {
    try {
      setLoading(true);
      const csrfToken = await fetchCsrfToken();
      if (!csrfToken) return;

      const apiUrl = FormatUrl("/create-connected-account/");
      const payload = { email: user?.email, clinic_id: getClinicId(user) };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to create Stripe account");

      const { onboarding_url } = await response.json();
      if (onboarding_url) {
        window.location.href = onboarding_url;
      } else {
        toast.error(t("stripe_payment.onboarding_link_failed"));
      }
    } catch (error) {
      console.error("Error starting Stripe onboarding:", error);
      toast.error(t("stripe_payment.onboarding_failed"));
    } finally {
      setLoading(false);
    }
  };

  const prevStatus = useRef(null); 
  const toastShown = useRef(false); 
  const apiCallRef = useRef(false);

  const handleSuccessRedirect = useCallback(async (clinicId) => {

    if (apiCallRef.current) return; 

    try {
      setLoading(true);
      const finalClinicId = clinicId ?? getClinicId(user);
      const apiUrl = FormatUrl(`/stripe-onboarding/success?clinic_id=${finalClinicId}`);
      const response = await fetch(apiUrl, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
      });
  
      if (!response.ok) throw new Error("Failed to verify onboarding status");
  
      const data = await response.json();
      setAccountData(data);
  
      if (data.charges_enabled) {
          if (prevStatus.current !== "connected") {
              setStatus("connected");
              prevStatus.current = "connected";
  
              if (!toastShown.current) {
                  toast.success(t("stripe_payment.onboarding_successful"));
                  toastShown.current = true;
              }
          }
      } else {
          if (prevStatus.current !== "not_connected") {
              setStatus("not_connected");
              prevStatus.current = "not_connected";
  
              if (!toastShown.current) {
                  toast.warn(t("stripe_payment.onboarding_incomplete"));
                  toastShown.current = true;
              }
          }
      }
      await checkStripeAccountStatus();
      apiCallRef.current = true;
    }catch (error) {
      console.error("Error handling Stripe success:", error);
      if (prevStatus.current !== "not_connected") {
        setStatus("not_connected");
        prevStatus.current = "not_connected";
  
        if (!toastShown.current) {
          toast.error(t("stripe_payment.onboarding_verification_error"));
          toastShown.current = true;
        }
      }
    } finally {
      setLoading(false);
    }
  }, [t,checkStripeAccountStatus, user]);
  

  const fetchOnboardingStatus = useCallback(async () => {
    if (apiCallRef.current) {
      return; 
    }
  
    try {
      const clinicIdFromUrl = getClinicId(user);
      if (clinicIdFromUrl) {
        await handleSuccessRedirect(clinicIdFromUrl);
      } else if (clinicIdFromUrl) {
        await checkStripeAccountStatus();
      } else {
        setLoading(false)
        return;
      }
  
      apiCallRef.current = true; 
      setLoading(false); 
  
    } catch (error) {
      console.error("[fetchOnboardingStatus] Error:", error);
      setLoading(false); 
      apiCallRef.current = false; 
    }
  }, [user, handleSuccessRedirect, checkStripeAccountStatus]);
  
  
  useEffect(() => {
    fetchOnboardingStatus();
  }, [fetchOnboardingStatus]);
  
  
  return (
    <div className="stripe-page">
      {/* Hero Section */}
      <section className="stripe-hero">
        <div className="stripe-hero-content">
          <h1 className="stripe-heading">{t("stripe_payment.title")}</h1>
          <p className="stripe-subtext">
          {t("stripe_payment.description")}
          </p>
        </div>
      </section>
  
      {/* Onboarding Section */}
      <div className="stripe-container">
        {loading || status === null ? (
          <div className="loading-wrapper">
            <FaSpinner className="loading-spinner" />
            <p className="loading-text">{t("stripe_payment.checking_status")}</p>
          </div>
        ) : (
          <div className="stripe-status">
            {status === "connected" ? (
              <div className="status-box success">
                <FaCheckCircle className="status-icon success-icon" />
                <h2>{t("stripe_payment.connected")} ✅</h2>
                <p className="account-info"><strong>{t("stripe_payment.account_id")}:</strong> {accountData?.account_id}</p>
                <p className="account-info"><strong>{t("stripe_payment.charges_enabled")}:</strong> {accountData?.charges_enabled ? "Yes" : "No"}</p>
                <p className="account-info"><strong>{t("stripe_payment.payouts_enabled")}:</strong> {accountData?.payouts_enabled ? "Yes" : "No"}</p>
                <button className="dashboard-btn" onClick={() => navigate("/")}>{t("stripe_payment.go_to_dashboard")}</button>
              </div>
            ) : (
              <div className="status-box error">
                <FaTimesCircle className="status-icon error-icon" />
                <h2>{t("stripe_payment.stripe_not_connected")} ❌</h2>
                <p className="account-info">{t("stripe_payment.onboarding_required")}</p>
              </div>
            )}
  
            {/* Buttons Section */}
            <div className="btn-group">
              {status === "connected" ? (
                <button className="stripe-btn success-btn" disabled>
                  <FaStripe className="icon" /> {t("stripe_payment.stripe_connected")}
                </button>
              ) : (
                <button className="stripe-btn connect-btn" onClick={startOnboarding} disabled={loading}>
                  <FaStripe className="icon" /> {t("stripe_payment.connect_with_stripe")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
  
};

export default StripeOnboarding;
